/* eslint-disable react/destructuring-assignment */
import clsx from 'clsx'
import React, { FC, useContext } from 'react'

import { useAmplitude } from '@Analytics/hook'
import SVGClose from '@Assets/SVG/Close'
import { Link } from '@Components/Link'
import { ProductContext } from '@Components/Product/Context'
import { ControllerContext } from '@Components/Product/Slider/Context/ControllerContext'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { AddToWishList } from '@Components/WishList/addToWishList'
import { Button } from '@CoreComponents/Buttons/Button'
import SliderControls from '@Domains/Product/Features/Display/SliderControls/SliderControls'
import SliderProgress from '@Domains/Product/Features/Display/SliderProgress/SliderProgress'
import { PagePresentation, PageType } from '@Generated/graphql'
import { CMSModalContext } from '@Globals/CMS/Modal/Context'
import { useTranslation } from '@Globals/Translations'

import styles from './SliderOverlay.module.scss'

const SliderOverlay: FC = () => {
  const { t } = useTranslation()
  const product = useContext(ProductContext)
  const {
    isZoomedIn,
    setZoomedIn,
  } = useContext(ControllerContext)
  const {
    activeSlide,
    isFullscreen,
    isSwipedUp,
    setActiveSlide,
    slides,
    toggleFullscreen,
  } = useContext(SliderContext)
  const [, openModal] = useContext(CMSModalContext)
  const ampli = useAmplitude()

  if (!product) {
    return null
  }

  const videoKey = slides.findIndex(slide => slide.tags?.includes('video'))
  const activeLookID = slides[activeSlide.key]?.tags?.find(tag => tag.startsWith('look:'))?.replace('look:', '') || ''

  return (
    <>
      <div
        className={clsx(
          styles.actions,
          activeSlide.key === videoKey && styles.hidden,
          isFullscreen && styles.fullscreen,
        )}
      >
        {isZoomedIn && (
          <Button
            className={clsx(styles.action, styles.exitZoom)}
            onClick={() => setZoomedIn(false)}
            theme="white"
            type="button"
          >
            {t('common:ZOOM_OUT')}
          </Button>
        )}

        {!isSwipedUp && isFullscreen && (
          <button
            className={clsx(styles.action, styles.closeButton)}
            onClick={toggleFullscreen}
            type="button"
          >
            {' '}
            <SVGClose />
          </button>
        )}

        <div className={clsx(styles.action, styles.wishListContainer)}>
          <AddToWishList className={styles.wishlistButton} id={product.id} />
        </div>

        {videoKey !== -1 && (
          <button
            aria-label="Play"
            className={clsx(styles.action, styles.play)}
            onClick={() => {
              ampli.galleryInteracted({
                asset_position: videoKey + 1,
                asset_type: slides[videoKey]?.tags?.includes('video') ? 'video' : 'image',
                has_look: slides?.some(slide => slide?.tags?.includes('look')),
                has_video: slides?.some(slide => slide?.tags?.includes('video')),
                type: 'play_video',
              })

              setActiveSlide({
                key: videoKey,
                recalculate: true,
              })
            }}
            type="button"
          >
            <svg version="1.1" viewBox="0 0 13 14.2" xmlns="http://www.w3.org/2000/svg">
              <path d="M0,14.2V0l13,7.1L0,14.2ZM1,1.7v10.9l9.9-5.4L1,1.7Z" />
            </svg>
          </button>
        )}

        {activeLookID && (
          <Button
            className={clsx(styles.action, styles.lookButton)}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              openModal(PageType.Look, activeLookID, 'product_display_gallery', PagePresentation.Sidebar)
            }}
            onDoubleClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            theme="white"
            type="button"
          >
            {t('common:SHOP_THE_LOOK')}
          </Button>
        )}
      </div>

      {product.brand && (
        <Link className={styles.brand} href={product.brand.url?.toString()}>
          <img
            alt={product.brand.id || ''}
            src={product.brand.logo || ''}
            title={product.brand.title || ''}
          />
        </Link>
      )}

      <SliderControls />
      {slides.length < 3 && (
        <SliderProgress />
      )}
    </>
  )
}

export default SliderOverlay
